import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import FiveColumnTable from "@/charts/FiveColumnTable";
import useLoadData from "@/hooks/useLoadData";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { showApiError, showSuccess } from "@/utils";

const BrandModal = ({
  isEdit = false,
  brandFormData,
  handleBrandInputChange,
  isBrandSubmitting,
  setShowAddBrandModal,
  setShowEditBrandModal,
  resetBrandForm,
  handleBrandSubmit,
  handleBrandEditSubmit,
  allClients,
  allClientsLoaded,
}) => {
  const { t } = useTranslation();
  const modalTitle = isEdit
    ? t("superAdmin.brands.editBrand", "Edit Brand")
    : t("superAdmin.brands.addNewBrand", "Add New Brand");
  const submitHandler = isEdit ? handleBrandEditSubmit : handleBrandSubmit;
  const submitText = isEdit
    ? t("superAdmin.brands.updateBrand", "Update Brand")
    : t("superAdmin.brands.createBrand", "Create Brand");
  const loadingText = isEdit
    ? t("common.editing", "Updating...")
    : t("common.adding", "Creating...");

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{modalTitle}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                if (isEdit) {
                  setShowEditBrandModal(false);
                } else {
                  setShowAddBrandModal(false);
                }
                resetBrandForm();
              }}
              disabled={isBrandSubmitting}
            ></button>
          </div>
          <form onSubmit={submitHandler}>
            <div className="modal-body">
              {/* Client ID Dropdown */}
              <div className="mb-3">
                <label htmlFor="client-id" className="form-label">
                  {t("superAdmin.brands.clientId", "Client ID")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <CustomDropdown
                  triggerId="client-id"
                  options={[
                    {
                      value: "",
                      label: t(
                        "superAdmin.brands.selectClient",
                        "Select a client",
                      ),
                    },
                    ...(allClientsLoaded && allClients
                      ? allClients.map((client) => ({
                          value: client.client_id,
                          label: client.client_id,
                        }))
                      : []),
                  ]}
                  value={brandFormData.client_id}
                  onChange={(val) =>
                    handleBrandInputChange({
                      target: { name: "client_id", value: val },
                    })
                  }
                  disabled={isBrandSubmitting || isEdit}
                />
                {isEdit && (
                  <small className="text-muted">
                    {t(
                      "superAdmin.brands.clientIdCannotChange",
                      "Client ID cannot be changed during edit",
                    )}
                  </small>
                )}
              </div>

              {/* Brand Type Selection */}
              <div className="mb-3">
                <label className="form-label">
                  {t("superAdmin.brands.brandType", "Brand Type")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <div className="btn-group w-100" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="brand_type"
                    id="brand-type-own"
                    value="own"
                    checked={brandFormData.brand_type === "own"}
                    onChange={handleBrandInputChange}
                    disabled={isBrandSubmitting}
                    required
                  />
                  <label
                    className="btn btn-outline-primary"
                    htmlFor="brand-type-own"
                    style={{
                      borderColor: "var(--brand-color-dark)",
                      color:
                        brandFormData.brand_type === "own"
                          ? "#fff"
                          : "var(--brand-color-dark)",
                      backgroundColor:
                        brandFormData.brand_type === "own"
                          ? "var(--brand-color-dark)"
                          : "transparent",
                    }}
                  >
                    {t("superAdmin.brands.ownBrand", "Own Brand")}
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="brand_type"
                    id="brand-type-competitor"
                    value="competitor"
                    checked={brandFormData.brand_type === "competitor"}
                    onChange={handleBrandInputChange}
                    disabled={isBrandSubmitting}
                    required
                  />
                  <label
                    className="btn btn-outline-primary"
                    htmlFor="brand-type-competitor"
                    style={{
                      borderColor: "var(--brand-color-dark)",
                      color:
                        brandFormData.brand_type === "competitor"
                          ? "#fff"
                          : "var(--brand-color-dark)",
                      backgroundColor:
                        brandFormData.brand_type === "competitor"
                          ? "var(--brand-color-dark)"
                          : "transparent",
                    }}
                  >
                    {t("superAdmin.brands.competitorBrand", "Competitor Brand")}
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="brand-name" className="form-label">
                  {t("superAdmin.brands.brandName", "Brand Name")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="brand-name"
                  name="name"
                  value={brandFormData.name}
                  onChange={handleBrandInputChange}
                  required
                  disabled={isBrandSubmitting}
                  placeholder={t(
                    "superAdmin.brands.enterBrandName",
                    "Enter brand name",
                  )}
                  style={{ lineHeight: "1.5" }}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="brand-domains" className="form-label">
                  {t("superAdmin.brands.domains", "Domains")}
                </label>
                <textarea
                  className="form-control"
                  id="brand-domains"
                  name="domains"
                  value={brandFormData.domains}
                  onChange={handleBrandInputChange}
                  disabled={isBrandSubmitting}
                  placeholder="example.com, another-domain.com"
                  style={{
                    lineHeight: "1.6",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  rows="3"
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.brands.domainsHint",
                    "Enter domains separated by commas",
                  )}
                </small>
              </div>
              <div className="mb-3">
                <label htmlFor="brand-name-variations" className="form-label">
                  {t(
                    "superAdmin.brands.brandNameVariationsDesc",
                    "Brand name variations - These keywords are treated as synonyms or variations of the brand name and will be used to highlight brand mentions.",
                  )}
                </label>
                <textarea
                  className="form-control"
                  id="brand-name-variations"
                  name="brand_name_variations"
                  value={brandFormData.brand_name_variations}
                  onChange={handleBrandInputChange}
                  disabled={isBrandSubmitting}
                  placeholder="keyword1, keyword2, keyword3"
                  style={{
                    lineHeight: "1.6",
                    minHeight: "80px",
                    resize: "vertical",
                  }}
                  rows="3"
                />
                <small className="text-muted">
                  {t(
                    "superAdmin.brands.brandNameVariationsHint",
                    "Enter brand name variations separated by commas",
                  )}
                </small>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  if (isEdit) {
                    setShowEditBrandModal(false);
                  } else {
                    setShowAddBrandModal(false);
                  }
                  resetBrandForm();
                }}
                disabled={isBrandSubmitting}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  isBrandSubmitting ||
                  !brandFormData.name.trim() ||
                  !brandFormData.client_id ||
                  !brandFormData.brand_type
                }
                style={{
                  backgroundColor: "var(--btn-solid-bg)",
                  color: "var(--btn-solid-text)",
                }}
              >
                {isBrandSubmitting ? loadingText : submitText}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

function SuperAdminBrand() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [allClients, allClientsLoaded] = useLoadData(
    `${BACKEND_URL}/api/v1/client/all`,
  );

  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [showEditBrandModal, setShowEditBrandModal] = useState(false);
  const [isBrandSubmitting, setIsBrandSubmitting] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandFormData, setBrandFormData] = useState({
    client_id: "",
    brand_type: "",
    name: "",
    domains: "",
    brand_name_variations: "",
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refreshAllBrands = () => setRefreshTrigger((prev) => prev + 1);
  const [allBrands, allBrandsLoaded, allBrandErrors] = useLoadData(
    `${BACKEND_URL}/api/v1/brand/all?refresh=${refreshTrigger}`,
  );

  const handleBrandInputChange = (e) => {
    const { name, value } = e.target;
    setBrandFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetBrandForm = () => {
    setBrandFormData({
      client_id: "",
      brand_type: "",
      name: "",
      domains: "",
      brand_name_variations: "",
    });
    setEditingBrand(null);
  };

  const handleBrandSubmit = async (e) => {
    e.preventDefault();
    setIsBrandSubmitting(true);

    try {
      const brandData = {
        client_id: brandFormData.client_id,
        brand_type: brandFormData.brand_type,
        name: brandFormData.name.trim(),
        domains: brandFormData.domains
          ? brandFormData.domains
              .split(",")
              .map((domain) => domain.trim())
              .filter(Boolean)
          : [],
        brand_name_variations: brandFormData.brand_name_variations
          ? brandFormData.brand_name_variations
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : null,
      };

      const response = await fetch(`${BACKEND_URL}/api/v1/brand`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(brandData),
      });

      const result = await response.json();

      if (response.ok) {
        setShowAddBrandModal(false);
        resetBrandForm();
        refreshAllBrands();
        showSuccess(
          result.message ||
            t(
              "superAdmin.brands.brandCreatedSuccess",
              "Brand created successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.brands.brandCreateError",
              "Failed to create brand",
            ),
        );
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      showApiError(
        t(
          "superAdmin.brands.brandCreateError",
          "An error occurred while creating the brand",
        ),
      );
    } finally {
      setIsBrandSubmitting(false);
    }
  };

  const handleBrandEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingBrand) return;

    setIsBrandSubmitting(true);

    try {
      const brandData = {
        client_id: brandFormData.client_id,
        brand_type: brandFormData.brand_type,
        name: brandFormData.name.trim(),
        domains: brandFormData.domains
          ? brandFormData.domains
              .split(",")
              .map((domain) => domain.trim())
              .filter(Boolean)
          : [],
        brand_name_variations: brandFormData.brand_name_variations
          ? brandFormData.brand_name_variations
              .split(",")
              .map((keyword) => keyword.trim())
              .filter(Boolean)
          : null,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/v1/brand/${encodeURIComponent(editingBrand.name)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(brandData),
        },
      );

      const result = await response.json();

      if (response.ok) {
        setShowEditBrandModal(false);
        resetBrandForm();
        refreshAllBrands();
        showSuccess(
          result.message ||
            t(
              "superAdmin.brands.brandUpdatedSuccess",
              "Brand updated successfully!",
            ),
        );
      } else {
        showApiError(
          result.detail ||
            t(
              "superAdmin.brands.brandUpdateError",
              "Failed to update brand",
            ),
        );
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      showApiError(
        t(
          "superAdmin.brands.brandUpdateError",
          "An error occurred while updating the brand",
        ),
      );
    } finally {
      setIsBrandSubmitting(false);
    }
  };

  const handleBrandEdit = (brand) => {
    setEditingBrand(brand);

    // Determine brand_type by checking client's own_brand and competitor_brands
    let brandType = "";
    const client = allClients?.find((c) => c.client_id === brand.client_id);
    if (client) {
      if (client.own_brand === brand.name) {
        brandType = "own";
      } else if (client.competitor_brands?.includes(brand.name)) {
        brandType = "competitor";
      }
    }

    setBrandFormData({
      client_id: brand.client_id || "",
      brand_type: brandType,
      name: brand.name || "",
      domains: brand.domains ? brand.domains.join(", ") : "",
      brand_name_variations: brand.brand_name_variations
        ? brand.brand_name_variations.join(", ")
        : "",
    });
    setShowEditBrandModal(true);
  };

  const handleBrandDelete = async (brand) => {
    if (
      window.confirm(
        t(
          "superAdmin.brands.deleteConfirm",
          `Are you sure you want to delete ${brand.name}?`,
          { name: brand.name },
        ),
      )
    ) {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/brand/${encodeURIComponent(brand.name)}?client_id=${encodeURIComponent(brand.client_id)}`,
          {
            method: "DELETE",
            credentials: "include",
          },
        );

        const result = await response.json();

        if (response.ok) {
          refreshAllBrands();
          showSuccess(
            result.message ||
              t(
                "superAdmin.brands.brandDeletedSuccess",
                "Brand deleted successfully!",
              ),
          );
        } else {
          showApiError(
            result.detail ||
              t(
                "superAdmin.brands.brandDeleteError",
                "Failed to delete brand",
              ),
          );
        }
      } catch (error) {
        console.error("Error deleting brand:", error);
        showApiError(
          t(
            "superAdmin.brands.brandDeleteError",
            "An error occurred while deleting the brand",
          ),
        );
      }
    }
  };

  // Filter brands based on search term and add action buttons
  const filteredBrands = useMemo(() => {
    let brands = allBrands;

    if (brandSearchTerm.trim() && allBrands) {
      brands = allBrands.filter((brand) => {
        const searchLower = brandSearchTerm.toLowerCase();
        const brandName = brand.name ? brand.name.toLowerCase() : "";
        const clientId = brand.client_id ? brand.client_id.toLowerCase() : "";

        if (brandName.includes(searchLower)) return true;
        if (clientId.includes(searchLower)) return true;

        if (brand.domains && Array.isArray(brand.domains)) {
          const domainMatch = brand.domains.some((domain) =>
            domain.toLowerCase().includes(searchLower),
          );
          if (domainMatch) return true;
        }

        if (
          brand.brand_name_variations &&
          Array.isArray(brand.brand_name_variations)
        ) {
          const keywordMatch = brand.brand_name_variations.some((keyword) =>
            keyword.toLowerCase().includes(searchLower),
          );
          if (keywordMatch) return true;
        }

        return false;
      });
    }

    // Add actions to each brand (5th column)
    return brands?.map((brand) => ({
      ...brand,
      actions: (
        <div className="d-flex gap-1">
          <button
            className="btn btn-sm btn-outline-primary border-0 p-1"
            onClick={() => handleBrandEdit(brand)}
            title={t("common.edit", "Edit")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-pencil"></i>
          </button>
          <button
            className="btn btn-sm btn-outline-danger border-0 p-1"
            onClick={() => handleBrandDelete(brand)}
            title={t("common.delete", "Delete")}
            style={{ minWidth: "28px", height: "28px" }}
          >
            <i className="bi bi-trash"></i>
          </button>
        </div>
      ),
    }));
    // eslint-disable-next-line
  }, [allBrands, brandSearchTerm, allClients, t]);

  return (
    <div>
      <h4 className="h5 fw-bold text-dark mb-2">
        {t("superAdmin.brands.title", "Brand Management")}
      </h4>
      <small className="text-muted mt-1 p-1" style={{ fontSize: "0.7rem" }}>
        {t(
          "superAdmin.brands.hint",
          "Before Adding a brand check if the brand or brand with similar name exists",
        )}
      </small>

      <div className="mb-3 d-flex align-items-center gap-3">
        <input
          type="text"
          className="form-control"
          placeholder={t(
            "superAdmin.brands.searchPlaceholder",
            "Search brands...",
          )}
          value={brandSearchTerm}
          onChange={(e) => setBrandSearchTerm(e.target.value)}
          style={{ maxWidth: 500 }}
        />
        <div className="d-flex flex-column align-items-start">
          <button
            className="btn btn-light"
            style={{
              color: "var(--brand-color-dark)",
              borderColor: "var(--brand-color-dark)",
            }}
            onClick={() => setShowAddBrandModal(true)}
          >
            <i className="bi bi-plus-circle"> </i>{" "}
            {t("superAdmin.brands.addBrand", "Brand")}
          </button>
        </div>
      </div>

      <FiveColumnTable
        data={filteredBrands}
        isLoaded={allBrandsLoaded}
        error={allBrandErrors}
        column1Key={"client_id"}
        column1Title={t("superAdmin.brands.clientId", "Client ID")}
        column2Key={"name"}
        column2Title={t("superAdmin.brands.brandName", "Brand Name")}
        column3Key={"domains"}
        column3Title={t("superAdmin.brands.domains", "Domains")}
        column4Key={"brand_name_variations"}
        column4Title={t(
          "superAdmin.brands.brandNameVariations",
          "Brand Name Variations",
        )}
        column5Key={"actions"}
        column5Title={t("common.actions", "Actions")}
        columnRatio="20-15-30-30-5"
      />

      {/* Add Brand Modal */}
      {showAddBrandModal && (
        <BrandModal
          isEdit={false}
          brandFormData={brandFormData}
          handleBrandInputChange={handleBrandInputChange}
          isBrandSubmitting={isBrandSubmitting}
          setShowAddBrandModal={setShowAddBrandModal}
          setShowEditBrandModal={setShowEditBrandModal}
          resetBrandForm={resetBrandForm}
          handleBrandSubmit={handleBrandSubmit}
          handleBrandEditSubmit={handleBrandEditSubmit}
          allClients={allClients}
          allClientsLoaded={allClientsLoaded}
        />
      )}

      {/* Edit Brand Modal */}
      {showEditBrandModal && (
        <BrandModal
          isEdit={true}
          brandFormData={brandFormData}
          handleBrandInputChange={handleBrandInputChange}
          isBrandSubmitting={isBrandSubmitting}
          setShowAddBrandModal={setShowAddBrandModal}
          setShowEditBrandModal={setShowEditBrandModal}
          resetBrandForm={resetBrandForm}
          handleBrandSubmit={handleBrandSubmit}
          handleBrandEditSubmit={handleBrandEditSubmit}
          allClients={allClients}
          allClientsLoaded={allClientsLoaded}
        />
      )}
    </div>
  );
}

export default SuperAdminBrand;
